"use client";

import React, { useState, useEffect } from "react";
import BookingTable from "../common/Table";
import {
  getVehicleAmenitiesList,
  useUpdateAmenitiesStatus,
  useAddAmenities,
  useUpdateAmenitiesData,
} from "@/hooks/useCommon";
import {
  X,
  Trash2,
  Edit,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Plus,
  Search,
  Toilet,
  Music,
  Refrigerator,
  Droplets,
  Newspaper,
  Cable,
  Wind,
  Armchair,
  FireExtinguisher,
  SunSnow,
  AirVent,
  Cog,
  Wifi,
  Tv,
  CarTaxiFront,
} from "lucide-react";
import DynamicForm from "./Popup";
import Button from "./Button";
import { toast } from "react-toastify";

interface TableData {
  sno: number;
  amenities_name: string;
  display_order: string;
  vehicleType: string;
  createdBy: string;
  status: string;
  id: number;
}

const ManageVehicleAmenities = () => {
  const [vehicleType, setVehicleType] = useState("");
  const [activeStatus, setActiveStatus] = useState(true);
  const [inactiveStatus, setInactiveStatus] = useState(false);
  const [currentRow, setCurrentRow] = useState<TableData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { data: vehicleDetail, refetch } = getVehicleAmenitiesList();
  const [rawVehicleData, setRawVehicleData] = useState<any[]>([]);
  const [filteredVehicleData, setFilteredVehicleData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const { mutate: updateStatusMutate } = useUpdateAmenitiesStatus();
  const { mutate: addVehicleTypeMutate } = useAddAmenities();
  const { mutate: updateStatusMutateData } = useUpdateAmenitiesData();
  
  useEffect(() => {
    refetch();
  }, []);
  useEffect(() => {
    if (Array.isArray(vehicleDetail)) {
      setRawVehicleData(vehicleDetail);
      setFilteredVehicleData(vehicleDetail);
    }
  }, [vehicleDetail]);

  const amenitiesIcons = [
    { key: "washroom", icon: "Toilet" },
    { key: "music", icon: "Music" },
    { key: "chillar", icon: "Refrigerator" },
    { key: "water bottle", icon: "Droplets" },
    { key: "news paper", icon: "Newspaper" },
    { key: "mobile & laptop charger", icon: "Cable" },
    { key: "air pump", icon: "Wind" },
    { key: "baby sitting", icon: "Armchair" },
    { key: "oxygen cylinder", icon: "Droplets" },
    { key: "fire extinguisher", icon: "FireExtinguisher" },
    { key: "tissue box", icon: "Cable" },
    { key: "carrier", icon: "Wind" },
    { key: "automatic cooling", icon: "SunSnow" },
    { key: "air conditioning", icon: "AirVent" },
    { key: "manual transmission", icon: "Cog" },
    { key: "automatic transmission", icon: "Cog" },
    { key: "wifi", icon: "Wifi" },
    { key: "tv", icon: "TV" },
    { key: "default", icon: "CarTaxiFront" },
  ];
  const iconComponentMap: Record<string, React.ElementType> = {
    Toilet,
    Music,
    Refrigerator,
    Droplets,
    Newspaper,
    Cable,
    Wind,
    Armchair,
    FireExtinguisher,
    SunSnow,
    AirVent,
    Cog,
    Wifi,
    TV: Tv,
    CarTaxiFront,
  };

  const vechicleData = Array.isArray(filteredVehicleData)
    ? filteredVehicleData.map((item: any, index: number) => ({
        sno: index + 1,
        display_order: item.display_order,
        id: item.id,
        status: item.status,
        created_name: item.created_name,
        amenities_name: item.amenities_name,
        action: "View",
      }))
    : [];

  const handleSearch = () => {
    const filtered = rawVehicleData.filter((item) => {
      const matchVehicleType = vehicleType ? item.id == vehicleType : true;

      const matchStatus =
        activeStatus && inactiveStatus
          ? true
          : activeStatus
          ? item.status == 1
          : inactiveStatus
          ? item.status == 0
          : true;

      return matchVehicleType && matchStatus;
    });

    setFilteredVehicleData(filtered);
  };

  const handleFormSubmit = (formData: Record<string, any>, type: string) => {
    const { amenities_name, display_order, status, id } = formData;
    if (type == "update") {
      updateStatusMutateData(
        {
          amenities_name,
          display_order,
          status,
          id,
        },
        {
          onSuccess: (res) => {
            toast.success("Vehicle amenities updated successfully");
            setIsModalOpen(false);
            refetch();
            setCurrentRow(null);
          },
          onError: (err) => {
            const message = err?.message;
            toast.error(message);
          },
        }
      );
    } else {
      addVehicleTypeMutate(
        {
          amenities_name,
          display_order,
          status,
        },
        {
          onSuccess: (res) => {
            toast.success("Vehicle amenities added successfully");
            refetch();
            setIsAddModalOpen(false);
            setCurrentRow(null);
          },
          onError: (err) => {
            const message = err.message;
            toast.error(message);
          },
        }
      );
    }
  };

  const onStatusChanged = (status_id: number, id?: any) => {
    let type_ids = [id];
    if (!id) {
      if (selectedRowIds.length === 0) {
        toast.error("No rows selected");
        return;
      }

      const selectedRoutes = filteredVehicleData.filter((row) =>
        selectedRowIds.includes(row.id)
      );
      type_ids = selectedRoutes.map((row) => row.id);
    }
    updateStatusMutate(
      {
        status: status_id,
        id: type_ids,
      },
      {
        onSuccess: (data) => {
          if (data.status == "success") {
            toast.success(data.message);
          }
          refetch();
          setSelectedRowIds([]);
        },
        onError: (error) => {
          toast.error("Something want wrong");
        },
      }
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = filteredVehicleData.map((row) => row.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedRowIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const allRowsSelected =
    filteredVehicleData?.length > 0 &&
    selectedRowIds?.length === filteredVehicleData?.length;
  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={allRowsSelected}
        />
      ),
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRowIds.includes(row.id)}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "amenities_name", header: "Amenities Type", sortable: true },

    {
      key: "amenity_icon",
      header: "Icon",
      sortable: false,
      render: (_: any, row: any) => {
        const iconEntry = amenitiesIcons.find(
          (item) =>
            item.key.toLowerCase().trim() ===
            row.amenities_name.toLowerCase().trim()
        );
        const IconComponent =
          iconComponentMap[iconEntry?.icon || "CarTaxiFront"];
        return <IconComponent size={20} />;
      },
    },
    { key: "display_order", header: "Display Order", sortable: true },
    { key: "id", header: "id", sortable: true, hidden: true },
    { key: "created_name", header: "Created By", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: number | string) => {
        const isOnline = Number(value) == 1;
        return (
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                isOnline ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm font-medium">
              {/* {currentStatus?.label || "Unknown"} */}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => onStatusChanged(2, row?.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];
  const vehicleOptions = Array.isArray(vehicleDetail)
    ? vehicleDetail.map((item: any) => ({
        label: item.amenities_name,
        value: item.id,
      }))
    : [];

  const formFields: any = [
    {
      id: "amenities_name",
      label: "Amenities Name",
      type: "text",
      placeholder: "Vehicle Amenities",
      required: true,
      defaultValue: currentRow?.amenities_name || "",
      // options: vehicleOptions,
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "Select Status",
      required: true,
      defaultValue: currentRow?.status,
      options: [
        { value: "1", label: "Active" },
        { value: "0", label: "In-Active" },
      ],
    },
    {
      id: "display_order",
      label: "Display Order",
      type: "number",
      required: true,
      defaultValue: currentRow?.display_order || "",
    },
    {
      id: "id",
      hidden: true,
      defaultValue: currentRow?.id || "",
    },
  ];

  const handleClear = () => {
    setVehicleType("");
    setActiveStatus(true);
    setInactiveStatus(false);
    setFilteredVehicleData(rawVehicleData);
  };

  return (
    <div className="w-full bg-white">
      <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        {/* Vehicle Type */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Vehicle Type
          </label>
          <select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            <option value="">Select Vehicle Amenities</option>
            {vehicleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex items-center gap-4">
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="active"
                  checked={activeStatus}
                  onChange={(e) => setActiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setActiveStatus(!activeStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    activeStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      activeStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="active"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* In-Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="inactive"
                  checked={inactiveStatus}
                  onChange={(e) => setInactiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setInactiveStatus(!inactiveStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    inactiveStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      inactiveStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="inactive"
                className="text-sm text-gray-700 cursor-pointer"
              >
                In-Active
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleSearch}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search
          </Button>

          <Button
            size="sm"
            onClick={handleClear}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Clear
          </Button>
        </div>
      </div>
      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Vehicle Amenities
          </Button>

          <Button
            size="sm"
            onClick={() => onStatusChanged(1)}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Publish
          </Button>

          {/* Unpublish Button with Download Icon */}
          <Button
            size="sm"
            onClick={() => onStatusChanged(0)}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Unpublish
          </Button>

          {/* Delete Button with Trash Icon (Danger Variant) */}
          <Button
            size="sm"
            variant="danger"
            onClick={() => onStatusChanged(2)}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Delete
          </Button>
        </div>
        <BookingTable
          data={vechicleData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          itemsPerPage={10}
        />
      </div>

      {/* Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Update Vehicle Amenities
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={(formData: Record<string, any>) =>
                    handleFormSubmit(formData, "update")
                  }
                  submitText="Update Vehicle Type"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsAddModalOpen(false);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add New Amenities
                </h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields.map((field: any) => ({
                    ...field,
                    defaultValue: "",
                  }))}
                  onSubmit={(formData: Record<string, any>) =>
                    handleFormSubmit(formData, "add")
                  }
                  submitText="Add Vehicle Amenities"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageVehicleAmenities;
